import React, { FC, ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Stack from '@mui/material/Stack';
import { TextError } from './TextError';
import { Box, CircularProgress } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';

export type SelectOptions = {
  value: string;
  label: string;
  extra?: string | ReactNode;
  extraBefore?: string | ReactNode;
  url?: string;
  color?: string;
};

type InputSelectProps = SelectProps & {
  title?: string;
  value?: string | null;
  options: SelectOptions[];
  onChange?: (e: SelectChangeEvent<unknown>) => void;
  onClose?: () => void;
  inputError?: string;
  placeholder?: string;
  defaultValue?: number | string;
  colorTitle?: string;
  handleFetchMore: () => void;
  hasMore: boolean;
  onClickOption: (value: string) => void;
  styleOptionDropdown?: (value: string) => React.CSSProperties;
};
/**
 * @returns Element InputSelect
 */
export const InputSelectInfinite: FC<InputSelectProps> = ({
  title,
  value,
  options,
  onChange,
  onClose,
  required = false,
  inputError,
  placeholder,
  defaultValue,
  colorTitle,
  handleFetchMore,
  hasMore,
  onClickOption,
  styleOptionDropdown,
  ...rest
}) => {
  return (
    <FormControl fullWidth error={!!inputError} required={required}>
      {title && (
        <FormLabel>
          <Typography fontWeight={colorTitle ? 700 : 400} mb={1.25} color={colorTitle || '#818181'}>
            {title}
          </Typography>
        </FormLabel>
      )}
      <SelectStyled
        {...rest}
        displayEmpty
        fullWidth
        defaultValue={defaultValue || ''}
        IconComponent={ExpandMoreIcon}
        renderValue={
          !value && placeholder
            ? () => (
                <MenuItem style={{ padding: 0 }} disabled value="">
                  <em>{placeholder}</em>
                </MenuItem>
              )
            : () => (
                <MenuItem style={{ padding: 0 }}>
                  <TextStyled>
                    {options.find((item) => item.value === value)?.label || ''}
                  </TextStyled>
                </MenuItem>
              )
        }
        value={value || ''}
        variant="outlined"
        onChange={onChange}
        onClose={() => {
          if (onClose) onClose();
        }}>
        <Stack id={'id-table-clients'} sx={{ height: '300px', overflow: 'scroll' }}>
          <InfiniteScroll
            dataLength={options.length}
            next={handleFetchMore}
            hasMore={hasMore}
            scrollableTarget={'id-table-clients'}
            loader={
              <Box p={1}>
                <CircularProgress />
              </Box>
            }>
            <MenuItem disabled value="">
              <em>{placeholder}</em>
            </MenuItem>
            {options?.map((op: SelectOptions, index: number) => (
              <MenuItemStyled
                style={
                  styleOptionDropdown
                    ? styleOptionDropdown(`${op.value}`)
                    : { backgroundColor: op.value === value ? '#ccc' : 'unset' }
                }
                key={index}
                onClick={() => onClickOption(op.value)}>
                <Stack
                  width={'100%'}
                  alignItems={'center'}
                  justifyContent={op.extra ? 'space-between' : 'flex-start'}
                  flexDirection={'row'}>
                  {op.extraBefore && op.extraBefore}
                  <TextStyled>{op.label}</TextStyled>
                  {op.extra && (
                    <Typography color={'#000'} width={'max-content'}>
                      {op.extra}
                    </Typography>
                  )}
                </Stack>
              </MenuItemStyled>
            ))}
          </InfiniteScroll>
        </Stack>
      </SelectStyled>
      {inputError && (
        <FormHelperText error={!!inputError} sx={{ ml: 0, pt: 0.5, maxWidth: '100%' }}>
          <TextError errorText={inputError} />
        </FormHelperText>
      )}
    </FormControl>
  );
};

const SelectStyled = styled(Select)(() => ({
  minHeight: 56,
  width: '100%',
  '& .MuiInputBase-input': {
    backgroundColor: '#fff',
    padding: '16px 12px'
  },
  '.MuiMenu-list': {
    paddingBottom: 0
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderBottom: `1px solid #BABAB6`,
    boxShadow: 'inset 0 5px 10px rgba(0, 0, 0, 0.05)',
    borderRadius: 10
  },
  borderRadius: '12px'
}));

const MenuItemStyled = styled(MenuItem)(() => ({
  height: 54,
  borderTop: `1px solid #818181`
}));
const TextStyled = styled(Typography)(() => ({
  width: '90%',
  maxWidth: 'max-content',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontFamily: `'Avenir',sans-serif`,
  color: '#000'
}));
